import { useLayoutEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import Icon from './Icon';
import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';
import { usePhoneChrome } from '../shared/usePhoneChrome';
import { useIsMobile } from '../shared/useIsMobile';
import {
  TOAST_FADE_MS,
  messageText,
  type TitledMessage,
  type Toast,
} from '../shared/useToasts';

/**
 * The confirmation strip at the bottom of the screen.
 *
 * Deliberately not a MUI Snackbar: this needs an inline action link with an
 * icon, and it has to sit above the day-detail scrim, which is easier to
 * reason about as a plain fixed element.
 *
 * On a phone the strip is a card rather than a pill: the message wraps to two
 * lines (three when that is the only way to show more than two words of a
 * title) and the action gets its own row beneath it. What happened is always
 * shown in full; only the event title is shortened.
 */

/** Line height of a phone toast's message, which is what its lines are counted in. */
const PHONE_LINE_PX = 18;
const PHONE_PAD_X = 16;

const Bar = styled('div', { shouldForwardProp: (p) => p !== '$visible' && p !== '$phone' })<{
  $visible: boolean;
  /** The phone chrome, where the toggle owns the bottom of the screen. */
  $phone?: boolean;
}>(({ theme, $visible, $phone }) => ({
    position: 'fixed',
    left: '50%',
    // Anchored above the toggle's band on a phone, and growing upward from
    // there, so a two- or three-line toast can never reach the pill.
    bottom: 'var(--toggle-band, 26px)',
    // Under the toggle (1270) and the account page (1260) rather than over
    // them. At 2000 it painted over both. A toast raised while a dialog is
    // already open now sits under the dialog, which is rare and harmless.
    zIndex: $phone ? 1240 : 2000,
    transform: `translateX(-50%) translateY(${$visible ? 0 : 8}px)`,
    opacity: $visible ? 1 : 0,
    transition: `opacity ${TOAST_FADE_MS}ms ease, transform ${TOAST_FADE_MS}ms ease`,
    pointerEvents: $visible ? 'auto' : 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    maxWidth: 'min(92vw, 620px)',
    padding: '11px 20px',
    borderRadius: 999,
    background: BRAND.parchment,
    color: BRAND.mocha,
    fontFamily: FONTS.body,
    fontSize: 13,
    fontWeight: 600,
    boxShadow: '0 6px 24px rgba(0,0,0,0.42)',
    [theme.breakpoints.down('sm')]: {
      // Centred by margins rather than a transform, so a wrapping message can
      // use the whole width instead of the half left of `left: 50%`.
      left: 0,
      right: 0,
      width: 'fit-content',
      maxWidth: '92vw',
      margin: '0 auto',
      transform: `translateY(${$visible ? 0 : 8}px)`,
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 6,
      padding: `11px ${PHONE_PAD_X}px`,
      borderRadius: 16,
    },
  }),
);

const Text = styled('span', { shouldForwardProp: (p) => p !== '$lines' })<{ $lines: number }>(
  ({ theme, $lines }) => ({
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      lineHeight: `${PHONE_LINE_PX}px`,
      // A backstop only: the fitting below already keeps within these lines.
      display: '-webkit-box',
      WebkitLineClamp: $lines,
      WebkitBoxOrient: 'vertical',
    },
  }),
);

/** An invisible copy of the message at its widest, for counting lines. */
const Measure = styled('span')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: `calc(92vw - ${PHONE_PAD_X * 2}px)`,
  visibility: 'hidden',
  pointerEvents: 'none',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  lineHeight: `${PHONE_LINE_PX}px`,
});

const Action = styled('button')(({ theme }) => ({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  flexShrink: 0,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 700,
  color: BRAND.mocha,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  '&:hover': { opacity: 0.7 },
  [theme.breakpoints.down('sm')]: { alignSelf: 'flex-end' },
}));

/** Words that end a shortened title badly, trimmed before the ellipsis. */
const DANGLING = /[\s,;:.\-–—|/&+]+$/;

function countWords(text: string): number {
  return text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
}

/**
 * The most of a title that fits with the rest of its message.
 *
 * Tries two lines first, cutting the title at a whole word. If that leaves two
 * words or fewer, three lines are allowed so the title says enough to be
 * recognised. `fits` answers whether a text sits within a number of lines.
 */
function fitTitle(
  message: TitledMessage,
  fits: (text: string, lines: number) => boolean,
): { text: string; lines: number } {
  const words = message.title.trim().split(/\s+/).filter(Boolean);
  const cut = (count: number) => words.slice(0, count).join(' ').replace(DANGLING, '');
  const compose = (count: number) =>
    count >= words.length
      ? messageText(message)
      : `${message.before}${cut(count)}…${message.after}`;
  const longest = (lines: number) => {
    for (let count = words.length; count > 0; count--) {
      if (fits(compose(count), lines)) return count;
    }
    return 0;
  };

  const inTwo = longest(2);
  if (inTwo === words.length || countWords(cut(inTwo)) > 2) {
    return { text: compose(inTwo), lines: 2 };
  }
  return { text: compose(longest(3)), lines: 3 };
}

interface Props {
  toast: Toast | null;
  onGoToSchedule: (eventId: string, dayKey: string) => void;
  onUndo: () => void;
  onUnhide: (eventId: string) => void;
}

export default function ToastBar({ toast, onGoToSchedule, onUndo, onUnhide }: Props) {
  const phone = useIsMobile();
  const phoneChrome = usePhoneChrome();
  const measure = useRef<HTMLSpanElement>(null);
  const [fitted, setFitted] = useState<{ id: number; text: string; lines: number } | null>(null);

  const message = toast?.message;
  const id = toast?.id;
  useLayoutEffect(() => {
    const el = measure.current;
    if (!phone || id === undefined || !message || typeof message === 'string' || !el) return;
    const fits = (text: string, lines: number) => {
      el.textContent = text;
      return el.getBoundingClientRect().height <= lines * PHONE_LINE_PX + 1;
    };
    setFitted({ id, ...fitTitle(message, fits) });
  }, [phone, id, message]);

  if (!toast) return null;
  const { action } = toast;
  const shown = phone && fitted?.id === toast.id ? fitted : null;

  return (
    <Bar $visible={toast.visible} $phone={phoneChrome} role="status" aria-live="polite">
      {phone && <Measure ref={measure} aria-hidden="true" />}
      <Text $lines={shown?.lines ?? 2}>{shown?.text ?? messageText(toast.message)}</Text>

      {action?.kind === 'goToSchedule' && (
        <Action type="button" onClick={() => onGoToSchedule(action.eventId, action.dayKey)}>
          Go to My Schedule
          <Icon name="ui:arrowUpRight" size={11} />
        </Action>
      )}

      {action?.kind === 'undo' && (
        <Action type="button" onClick={onUndo}>
          Undo
        </Action>
      )}

      {action?.kind === 'unhide' && (
        <Action type="button" onClick={() => onUnhide(action.eventId)}>
          Undo
        </Action>
      )}
    </Bar>
  );
}
